// Sidebar.jsx
import React from "react";
import {
    BarChart3Icon,
    UsersIcon,
    BriefcaseIcon,
    UserCheckIcon,
    SettingsIcon,
    FileTextIcon,
    CalendarIcon,
    ClockIcon,
    UserIcon,
    TargetIcon,
    CreditCardIcon,
    BellIcon,
    ChevronDownIcon,
    ChevronUpIcon
} from 'lucide-react';

const Sidebar = ({
    // primary API
    activeTab,
    setActiveTab,
    // aliases to support simpler callers
    activeId,
    onSelect,
    // section expansion state (optional)
    expandedSections: expandedSectionsProp,
    toggleSection: toggleSectionProp,
    isOpen = true,
    onItemClick,
    onOverlayClick,
    items
}) => {
    // Backward/forward compatibility for prop names
    const currentActive = typeof activeTab !== 'undefined' ? activeTab : (activeId || 'overview');
    const setCurrentActive = typeof setActiveTab === 'function' ? setActiveTab : (typeof onSelect === 'function' ? onSelect : () => {});
    const expandedSections = expandedSectionsProp || {};
    const toggleSection = typeof toggleSectionProp === 'function' ? toggleSectionProp : () => {};
    const sidebarItems = items || [
        {
            id: 'overview',
            label: 'Overview',
            icon: BarChart3Icon,
            section: 'main'
        },
        {
            section: 'hire',
            label: 'Hire',
            icon: BriefcaseIcon,
            items: [
                { id: 'jobs', label: 'Jobs', icon: BriefcaseIcon },
                { id: 'candidates', label: 'Candidates', icon: UsersIcon },
                { id: 'assessments', label: 'Assessments', icon: FileTextIcon, badge: true }
            ]
        },
        {
            section: 'manage',
            label: 'Manage',
            icon: UserCheckIcon,
            items: [
                { id: 'onboard', label: 'Onboard', icon: UserIcon },
                { id: 'people', label: 'People', icon: UsersIcon },
                { id: 'tracking', label: 'Tracking', icon: TargetIcon },
                { id: 'time-off', label: 'Time-off', icon: ClockIcon },
                { id: 'shift', label: 'Shift', icon: CalendarIcon },
                { id: 'roster', label: 'Roster', icon: FileTextIcon },
                { id: 'payroll', label: 'Payroll', icon: CreditCardIcon },
            ]
        },
        {
            section: 'retain',
            label: 'Retain',
            icon: TargetIcon,
            items: [
                { id: 'reviews', label: 'Reviews', icon: FileTextIcon },
                { id: 'goals', label: 'Goals', icon: TargetIcon }
            ]
        },
        {
            section: 'settings',
            label: 'Settings',
            icon: SettingsIcon,
            items: [
                { id: 'general', label: 'General', icon: SettingsIcon },
                { 
                    id: 'templates', 
                    label: 'Templates', 
                    icon: FileTextIcon,
                    items: [
                        { id: 'email-templates', label: 'Emails', icon: FileTextIcon },
                        { id: 'event-templates', label: 'Events', icon: CalendarIcon },
                        { id: 'benefit-templates', label: 'Benefits', icon: CreditCardIcon },
                        { id: 'holiday-templates', label: 'Holidays', icon: CalendarIcon },
                        { id: 'custom-fields', label: 'Custom Fields', icon: FileTextIcon }
                    ]
                },
                { id: 'page-setup', label: 'Page setup', icon: FileTextIcon },
                { id: 'billing', label: 'Billing', icon: CreditCardIcon },
                { id: 'team', label: 'Team', icon: UsersIcon },
                { id: 'profile', label: 'Profile', icon: UserIcon },
                { id: 'notifications', label: 'Notifications', icon: BellIcon },
                { id: 'hire-settings', label: 'Hire Settings', icon: BriefcaseIcon }
            ]
        }
    ];

    const renderSidebarItem = (item) => {
        if (item.section && item.section !== 'main') {
            const isExpanded = expandedSections[item.section];
            return (
                <div key={item.section} className="mb-2">
                    <button
                        onClick={() => toggleSection(item.section)}
                        className="w-full flex items-center justify-between px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <div className="flex items-center">
                            {(item.icon ? <item.icon className="w-5 h-5 mr-3" /> : <FileTextIcon className="w-5 h-5 mr-3" />)}
                            <span className="font-medium">{item.label}</span>
                        </div>
                        {isExpanded ? (
                            <ChevronUpIcon className="w-4 h-4" />
                        ) : (
                            <ChevronDownIcon className="w-4 h-4" />
                        )}
                    </button>
                    {isExpanded && (
                        <div className="ml-6 mt-1 space-y-1">
                            {item.items.map((subItem) => {
                                if (subItem.items) {
                                    // Handle nested items (like Templates)
                                    const isSubExpanded = expandedSections[`${item.section}-${subItem.id}`];
                                    return (
                                        <div key={subItem.id} className="space-y-1">
                                            <button
                                                onClick={() => toggleSection(`${item.section}-${subItem.id}`)}
                                                className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${
                                                    currentActive === subItem.id
                                                        ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                                                        : 'text-gray-600 hover:bg-gray-100'
                                                }`}
                                            >
                                                <div className="flex items-center">
                                                    {(subItem.icon ? <subItem.icon className="w-4 h-4 mr-3" /> : <FileTextIcon className="w-4 h-4 mr-3" />)}
                                                    <span>{subItem.label}</span>
                                                </div>
                                                {isSubExpanded ? (
                                                    <ChevronUpIcon className="w-4 h-4" />
                                                ) : (
                                                    <ChevronDownIcon className="w-4 h-4" />
                                                )}
                                            </button>
                                            {isSubExpanded && (
                                                <div className="ml-4 space-y-1">
                                                    {subItem.items.map((nestedItem) => (
                                                        <button
                                                            key={nestedItem.id}
                                                            onClick={() => {
                                                                setCurrentActive(nestedItem.id);
                                                                try { window.location.hash = `#${nestedItem.id}`; } catch {}
                                                                onItemClick && onItemClick(nestedItem.id);
                                                            }}
                                                            className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                                                                currentActive === nestedItem.id
                                                                    ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                                                                    : 'text-gray-600 hover:bg-gray-100'
                                                            }`}
                                                        >
                                                            {(nestedItem.icon ? <nestedItem.icon className="w-4 h-4 mr-3" /> : <FileTextIcon className="w-4 h-4 mr-3" />)}
                                                            <span>{nestedItem.label}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                return (
                                    <button
                                        key={subItem.id}
                                        onClick={() => {
                                            setCurrentActive(subItem.id);
                                            try { window.location.hash = `#${subItem.id}`; } catch {}
                                            onItemClick && onItemClick(subItem.id);
                                        }}
                                        className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                                            currentActive === subItem.id
                                                ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                                                : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        {(subItem.icon ? <subItem.icon className="w-4 h-4 mr-3" /> : <FileTextIcon className="w-4 h-4 mr-3" />)}
                                        <span>{subItem.label}</span>
                                        {subItem.badge && (
                                            <span className="ml-auto w-2 h-2 bg-orange-400 rounded-full"></span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        } else {
            return (
                <button
                    key={item.id}
                    onClick={() => {
                        setCurrentActive(item.id);
                        try { window.location.hash = `#${item.id}`; } catch {}
                        onItemClick && onItemClick(item.id);
                    }}
                    className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors mb-2 ${
                        currentActive === item.id
                            ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                            : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    {(item.icon ? <item.icon className="w-5 h-5 mr-3" /> : <FileTextIcon className="w-5 h-5 mr-3" />)}
                    <span className="font-medium">{item.label}</span>
                </button>
            );
        }
    };

    return (
        <>
            {/* Overlay for mobile when open */}
            <div
                className={`fixed inset-0 bg-black/30 z-30 md:hidden transition-opacity ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
                aria-hidden="true"
                onClick={onOverlayClick}
            />
            <aside className={`w-64 bg-white border-r border-gray-200 flex-shrink-0 fixed md:static inset-y-0 left-0 z-40 transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 transition-transform duration-300 ease-in-out h-full`}
                aria-label="Sidebar navigation">
                <div className="p-4 h-full overflow-y-auto">
                    <nav className="space-y-1">
                        {sidebarItems.map(renderSidebarItem)}
                    </nav>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;